<template>
    <el-container direction="vertical"
                  class="rel h-full">
        <div v-show="!displayedPhotos"
             class="_p-1 _mb-1 f-x-between">
            <el-radio-group v-model="viewMode"
                            size="mini">
                <el-radio-button label="days">
                    {{ labels.tabs.days }}
                </el-radio-button>
                <el-radio-button label="list">
                    {{ labels.tabs.list }}
                </el-radio-button>
            </el-radio-group>

            <div class="f space-x-0.5">
                <access permissions="project_images_edit"
                        hidable>
                    <el-button type="primary"
                               size="mini"
                               icon="el-icon-upload2"
                               @click="handlePhotoUpload">
                        {{ labels.btns.uploadPhoto }}
                    </el-button>
                </access>
                
                <filter-popover width="400"
                                popover-placement="bottom-end"
                                :value="filters"
                                :fields="fields"
                                no-fields-description="Нет фильтров"
                                @change="changeFilter"
                                @confirm="confirmFilter">
                    <template #button>
                        <el-button icon="el-icon-s-operation">
                            {{ labels.btns.filter }}
                        </el-button>
                    </template>
                </filter-popover>
            </div>
        </div>

        <!-- ListView -->
        <template v-if="isViewModeList">
            <!-- Photos -->
            <div v-if="photosState.photos.length"
                 ref="cardList"
                 :class="classes.photosContainer"
                 class="pdown-1"
                 @scroll="handleScroll">
                <div :class="classes.title">
                    <our-label :value="labels.photoTypes.allPhotos"
                               primary />
                    <our-label :value="titleDescription" />
                </div>
                
                <!-- Photo -->
                <div :class="classes.photos">
                    <card v-for="(photo, index) in photosState.photos"
                          :key="photo.id"
                          ref="cards"
                          :photo="photo"
                          :index="index"
                          deletable
                          @delete="handleDeletePhotoById" />
                </div>
            </div>

            <div v-else-if="loading"
                 v-loading="loading"
                 class="h-full" />

            <el-empty v-else
                      class="h-full"
                      :description="labels.emptyText" />
        </template>

        <div v-else-if="displayedPhotos"
             :class="classes.photosContainer">
            <div class="_px-1 _mt-1">
                <el-button class="font-medium"
                           type="text"
                           icon="el-icon-back"
                           @click="goBack">
                    Вернуться назад
                </el-button>
                <our-label :value="displayedPhotos.label"
                           class="_my-0.5 font-medium"
                           primary />
                <our-label :value="displayedPhotos.description"
                           color="black" />
            </div>

            <div :class="classes.photos"
                 class="p-0 _m-1">
                <card v-for="(photo, index) in displayedPhotos.photos"
                      :key="photo.id"
                      ref="cards"
                      :photo="photo"
                      :index="index" />
            </div>
        </div>

        <!-- Days view -->
        <template v-else>
            <div v-if="photosByDays?.length"
                 :class="classes.photosContainer">
                <div v-for="photos in photosByDays"
                     :key="photos.key"
                     class="_p-1 border-bottom-1 border-gray-100">
                    <our-label :value="photos.label"
                               class="_mb-1 font-medium"
                               primary />
                    
                    <div v-if="photos.object"
                         class="space-y-0.5 _mb-1">
                        <div class="f-y-center f-x-between">
                            <our-label :value="labels.photoTypes.object"
                                       color="black" />
                            <el-button v-if="photos.object.length > 5"
                                       type="text"
                                       @click="showAll(photos, 'object')">
                                {{ labels.btns.showAll }}
                            </el-button>
                        </div>
                        <div :class="classes.photosByDays(photos.object.length)">
                            <card v-for="(photo, index) in photos.object.slice(0, 5)" 
                                  :key="photo.id"
                                  :class="classes.photoArea(index)"
                                  :photo="photo"
                                  :index="index" />
                        </div>
                    </div>
                      
                    <div v-if="photos.territory"
                         class="space-y-0.5">
                        <div class="f-y-center f-x-between">
                            <our-label :value="labels.photoTypes.territory"
                                       color="black" />
                            <el-button v-if="photos.territory.length > 5"
                                       type="text"
                                       @click="showAll(photos, 'territory')">
                                {{ labels.btns.showAll }}
                            </el-button>
                        </div>
                        <div :class="classes.photosByDays(photos.territory.length)">
                            <card v-for="(photo, index) in photos.territory.slice(0, 5)" 
                                  :key="photo.id"
                                  :class="classes.photoArea(index)"
                                  :photo="photo"
                                  :index="index" />
                        </div>
                    </div>
                </div>
            </div>

            <div v-else-if="loading"
                 v-loading="loading"
                 class="h-full" />

            <el-empty v-else
                      class="h-full"
                      :description="labels.emptyText" />
        </template>

        <el-button v-if="canLoadMore"
                   type="primary"
                   class="abs-x-center b-1 depth-10"
                   round
                   :icon="loading ? 'el-icon-loading' : ''"
                   @click="loadPhotos">
            <span v-if="!loading">Показать еще</span>
        </el-button>
    </el-container>
</template>
<script>
import { mapActions, mapMutations } from 'vuex'
import { endOfDay, parse, startOfDay, subWeeks } from 'date-fns'

import { actionable } from '@/store/connectors'

import { formatForServer, labelAt } from '@/utils/date'
import { then } from '@/utils/immutable'
import { defineFilterField } from '@/utils/ui'

import Card from '@/components/photos/PhotoCard'
import FilterPopover from '@/components/popovers/FilterPopover'

export default {
  components: {
    Card,
    FilterPopover
  },

  mixins: [
    actionable({ on: 'photos', name: 'fetchPhotosSliced' })
  ],

  data() {
    return {
      loading: false,
      viewMode: 'days',

      photosSliced: {},

      ghostTitle: false,
      extremeCards: {},
      displayedPhotos: null,

      photoType: 'allPhotos',
      filters: {
        'jobTypes.id': this.$route.params.jobs,
        'recognitions_count': [
          then(this.$route.query.from, x => parse(x, { withTime: false })),
          then(this.$route.query.to, x => parse(x, { withTime: false }))
        ].filter(is)
      }
    }
  },

  computed: {
    labels() {
      return {
        tabs: {
          list: 'Списком',
          days: 'По дням'
        },
        btns: {
          uploadPhoto: 'Загрузить фото',
          filter: 'Фильтр',
          showAll: 'Смотреть все'
        },
        photoTypes: {
          allPhotos: 'Все фотографии',
          territory: 'Фото территории',
          object: 'Фото помещений'
        },
        fields: {
          image_shot: 'Дата съемки'
        },
        emptyText: 'Нет фотографий'
      }
    },

    classes() {
      return {
        photosContainer: {
          'rel overflow-auto with-custom-scrollbar-dark': true
        },
        title: {
          'f-col font-bold h-5 space-y-0.5 transition-all duration-500 border-box': true,
          'ghost-title _px-1 _pt-1 transition-all duration-500': this.ghostTitle,
          'fix': !this.ghostTitle
        },
        photos: {
          'cols-4 rows-min gap-0.5': true,
          '_pt-5': !this.ghostTitle
        },
        photosByDays: (length) => ({
          'cols-4 gap-0.5': true,
          'photos-by-days rows-2': length >= 5
        }),
        photoArea: (i) => ({
          [`photo-area-${i}`]: true
        })
      }
    },

    isViewModeList() {
      return this.viewMode === 'list'
    },

    titleDescription() {
      const { first, last } = this.extremeCards || {}
      const firstPhoto = labelAt(first?.photo?.image?.shot_at, { withTime: false }) || 'Неизвестно'
      const lastPhoto = labelAt(last?.photo?.image?.shot_at, { withTime: false }) || 'Неизвестно'

      return `${firstPhoto} - ${lastPhoto}`
    },

    photosByDays() {
      return Object.values(
        (this.photosSliced.data || [])
          .reduce((r, x) => {
            const key = x.image?.shot_at && this.prepareDate(x.image?.shot_at) || 'shot_at_none'
            const type = x.project_point_position?.house?.type
            
            return {
              ...r, 
              [key]: {
                ...r[key],
                key,
                label: key,
                [type]: [ ...r[key]?.[type] || [], x ]
              }
            }
          }, {})
      )
    },

    photosState() {
      return this.$store.getters['photos/photosState'](this.photoType);
    },
    currentPage() {
      return this.photosState.page.number;
    },
    disabled() {
      return this.loading || this.photosState.noMore;
    },

    fields() {
      return [
        defineFilterField({
          type: 'daterange',
          label: this.labels.fields.image_shot,
          from: value => value['recognitions_count'],
          to: value => ({ 'recognitions_count': value })
        })
      ]
    },

    canLoadMore() {
      return this.isViewModeList 
        ? !this.photosState.noMore 
        : !this.displayedPhotos && !this.filters.recognitions_count?.[0] && this.photosSliced?.meta?.next_at
    }
  },

  watch: {
    viewMode(mode) {
      this.ghostTitle = false
      this.clearPhotos()
      this.loadPhotos().then((data) => {
        if (mode === 'days' && this.canLoadMore && !data?.length) this.loadPhotos()
      })
    }
  },

  mounted() {
    this.clearPhotos()
    this.loadPhotos().then((data) => {
      if (!this.isViewModeList && this.canLoadMore && !data?.length) this.loadPhotos()
    })
  },
  
  methods: {
    ...mapMutations({
      setNoMore: 'photos/SET_NO_MORE',
      setPage: 'photos/SET_PAGE',
      showForm: 'form/SHOW_FORM',
      clearLoadedPhotos: 'photos/CLEAR_LOADED_PHOTOS'
    }),
    ...mapActions({
      getProfileLicense: 'auth/getProfileLicense'
    }),
    ...mapActions('photos', ['deleteById', 'getPhotos']),

    handleScroll(event) {
      if (event.target.scrollTop >= 80) {
        this.ghostTitle = true
      } else {
        this.ghostTitle = false
      }

      this.extremeCards = this.findExtremeVisibleElement()
    },

    findExtremeVisibleElement() {
      const container = this.$refs.cardList?.getBoundingClientRect()

      if (container) {
        return this.$refs.cards.reduce((r, card) => {
          const { top, bottom, height } = card.$el.getBoundingClientRect()
  
          if (top + height / 2 >= container.top && !r?.first) 
            return { first: card, last: card }
  
          if (bottom - height / 2 <= container.bottom)
            return { ...r, last: card }
  
          return r
        }, {})
      }
    },

    clearPhotos() {
      this.clearLoadedPhotos()
      this.photosSliced = {}
    },

    loadPhotos() {
      if (this.isViewModeList) {
  
        if (this.photosState.photos.length) {
          this.setPage({
            photoType: this.photoType,
            number: this.currentPage + 1
          })
        }
  
        this.loading = true;
        return this.getPhotos({
          photoType: this.photoType,
          payload: {params: this.createRequestParams()}
        })
          .then((data) => {
            if (this.isViewModeList) {
              this.extremeCards = this.findExtremeVisibleElement()
            }

            return data
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = true
        return this.fetchPhotosSliced(this.createRequestParams())
          .then(({ data }) => {
            this.photosSliced = { data: [ ...this.photosSliced.data || [], ...data.data.data ], meta: data.data.meta }

            return data.data.data
          })
          .catch(console.error)
          .finally(() => {
            this.loading = false
          })
      }
    },

    createRequestParams() {
      if (this.isViewModeList) {
        const filter = this.prepareFilter(this.filters)
  
        return {
          filter,
          append: [ 'unread_changes_exists', 'project_point_position' ],
          include: [ 'image', 'image.source_info' ],
          sort: '-image.shot_at',
          page: this.photosState.page
        }
      } else {
        const now = new Date()
        const from = this.filters.recognitions_count?.[0] 
          || (this.photosSliced.meta ? subWeeks(new Date(this.photosSliced.meta?.next_at), 1) : subWeeks(startOfDay(now), 1))
        const to = this.filters.recognitions_count?.[1] 
          || (this.photosSliced.meta ? new Date(this.photosSliced.meta?.next_at) : endOfDay(now))

        return {
          projectId: this.$route.params.projectId,
          from,
          to,
          filterByDateType: 'shot',
          withImage: true,
          withProjectPointPosition: true,
          sort: '-shot_at'
        }
      }
    },

    changeFilter(x) {
      this.filters = x
    },

    confirmFilter() {
      this.clearPhotos()
      this.loadPhotos().then((data) => {
        if (!this.isViewModeList && this.canLoadMore && !data?.length) this.loadPhotos()
      })
    },

    prepareFilter(x) {
      const r = { ...x }

      const from = then(r.recognitions_count?.[0], x => formatForServer(startOfDay(x, { zoned: false })))
      const to = then(r.recognitions_count?.[1], x => formatForServer(endOfDay(x, { zoned: false })))
          
      r['image.shot_at_from'] = from
      r['image.shot_at_to'] = to
      r['belongsToProject'] = this.$route.params.projectId

      delete r['recognitions_count']

      return r
    },

    prepareDate(x) {
      return then(x, x => labelAt(x, { withCapital: true, withTime: false, withAlias: true, zoned: false, iso: true }))
    },

    handlePhotoUpload() {
      this.showForm({
        formName: 'photo-upload-form',
        callback: () => {
          this.getProfileLicense()
          this.clearPhotos()
          this.loadPhotos()
        }
      });
    },

    handlePhotoEdit(image) {
      this.showForm({
        formName: 'photo-upload-form',
        payload: {photo_id: image.id}
      });
    },

    showAll(photos, type) {
      this.displayedPhotos = {
        label: photos.label,
        description: this.labels.photoTypes[type],
        photos: photos[type]
      }
    },

    goBack() {
      this.displayedPhotos = null
    },

    handleDeletePhotoById(photoId) {
      this.loading = true
      this.deleteById({ photoId })
        .catch(console.error)
        .finally(() => this.loading = false)
    }
  }
};
</script>

<style lang="scss">
.total-counter {
  position: absolute;
  border-radius: 4px;
  right: 60px;
  bottom: 16px;
  height: auto;
  z-index: 99;
  background-color: rgba(0, 0, 0, .8);
}

.ghost-title {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.5625) 8.28%, rgba(0, 0, 0, 0) 100%);
  backdrop-filter: blur(1px);
  
  p {
    color: white;
  } 
}

.photos-by-days {
  .photo-area-0 {
    grid-row: span 2;
    grid-column: span 2;
  }
}
</style>
